import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { data as cottagesData } from 'selectors/cottages';
import { fetchCottagesRequest } from 'actions/cottages';
import * as selectors from '../redux/selectors';
import {
  fetchHostFilesConfigRequest,
  fetchHostFilesRequest,
  fetchHostFilesCountsRequest,
  uploadHostFileRequest,
  viewHostFileRequest,
  downloadHostFileRequest,
  renameHostFileRequest,
  reassignHostFileRequest,
  deleteHostFileRequest,
  clearHostFilesStatus,
} from '../redux/actionCreators';

const EMPTY_COTTAGES = [];

const useHostFiles = () => {
  const dispatch = useDispatch();
  const config = useSelector(selectors.config);
  const filesList = useSelector(selectors.files);
  const counts = useSelector(selectors.counts);
  const groupedFiles = useSelector(selectors.groupedFiles);
  const isLoading = useSelector(selectors.isLoading);
  const isUploading = useSelector(selectors.isUploading);
  const uploadProgress = useSelector(selectors.uploadProgress);
  const uploadFileIndex = useSelector(selectors.uploadFileIndex);
  const uploadFileCount = useSelector(selectors.uploadFileCount);
  const isUpdating = useSelector(selectors.isUpdating);
  const error = useSelector(selectors.error);
  const success = useSelector(selectors.success);
  const cottages = useSelector(cottagesData);

  // Refs avoid refiring fetches when consumers remount with data already in the store.
  // Mutations are pushed into the store by the sagas, so re-fetching on remount is wasteful.
  const configRef = React.useRef(config);
  const filesRef = React.useRef(filesList);
  const countsRef = React.useRef(counts);
  const cottagesRef = React.useRef(cottages);
  configRef.current = config;
  filesRef.current = filesList;
  countsRef.current = counts;
  cottagesRef.current = cottages;

  React.useEffect(() => {
    if (!configRef.current || !configRef.current.max_files_per_slot) {
      dispatch(fetchHostFilesConfigRequest());
    }
    if (!filesRef.current || !filesRef.current.length) {
      dispatch(fetchHostFilesRequest());
    }
    if (!countsRef.current || !countsRef.current.listings) {
      dispatch(fetchHostFilesCountsRequest());
    }
    if (!cottagesRef.current || !cottagesRef.current.length) {
      dispatch(fetchCottagesRequest());
    }
  }, [dispatch]);

  const handleUpload = (payload) => dispatch(uploadHostFileRequest(payload));
  const handleView = (payload) => dispatch(viewHostFileRequest(payload));
  const handleDownload = (payload) => dispatch(downloadHostFileRequest(payload));
  const handleRename = (payload) => dispatch(renameHostFileRequest(payload));
  const handleReassign = (payload) => dispatch(reassignHostFileRequest(payload));
  const handleDelete = (payload) => dispatch(deleteHostFileRequest(payload));
  const handleClearStatus = () => dispatch(clearHostFilesStatus());

  return {
    config,
    files: filesList,
    counts,
    groupedFiles,
    cottages: cottages || EMPTY_COTTAGES,
    isLoading,
    isUploading,
    uploadProgress,
    uploadFileIndex,
    uploadFileCount,
    isUpdating,
    error,
    success,
    onUpload: handleUpload,
    onView: handleView,
    onDownload: handleDownload,
    onRename: handleRename,
    onReassign: handleReassign,
    onDelete: handleDelete,
    onClearStatus: handleClearStatus,
  };
};

export default useHostFiles;
